import * as React from "react";
import { useState } from "react";
import { CheckboxEditor } from "../../../../../../react-core/Html/CheckboxEditor";
import { RadiosEditor } from "../../../../../../react-core/Html/RadiosEditor";
import { MandateNameFuzzySearchClient } from "../../../../../../Wrap/Api/Mandate/MandateNameFuzzySearch";
import { SubAccountType } from "../../../../../../Wrap/Constants/Enums/Domain/SubAccountType";
import { AdvancedSearchFilterType } from "../../../../../../Wrap/Constants/Enums/Search/AdvancedSearchFilterType";
import { Mandate } from "../../../../../../Wrap/Models/Models/Mandate/Mandate";
import { Alert } from "../../../../../Shared/Components/Alert";
import { AlertType } from "../../../../../Shared/Entities/Constants/AlertConstants";
import { SearchInput } from "../../../../../Shared/SearchInput/SearchInput";
import { IAdvancedSearchQuery } from "../../ClientRecord/Action/AccountSearchAction";
import {
    advancedSearchFilterTypeArray,
    advancedSearchFilterTypeMap,
    productWrapperOptions,
} from "../../ClientRecord/Resource/Contents";
import { AdvancedQueryDropdown } from "./AdvancedQueryDropdown";

interface IAdvancedSearchProps {
    applyAdancedFilter: (query: IAdvancedSearchQuery[]) => void;
}

export const AdvancedSearch = (props: IAdvancedSearchProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const [showContent, setContent] = useState("dropdown");

    const [selectedWrappers, setSelectedWrappers] = useState([]);
    const [investedInMandate, setInvestedInMandate] = useState(null);
    const [mandateNames, setMandateNames] = useState([]);
    const [hasRegularPayments, setHasRegularPayments] = useState(null);
    const [hasRegularWithdrawals, setHasRegularWithdrawals] = useState(null);
    const [errorMsg, setErrorMsg] = useState<string[]>([]);

    const [advancedQueryArr, setAdvancedQueryArr] = useState([]);

    const applyFilter = (filterType: AdvancedSearchFilterType) => {
        setIsOpen(false);
        if (filterType == AdvancedSearchFilterType.Wrapper) {
            // remove old then add new
            const newAdvancedQueryArr =
                advancedQueryArr.filter(
                    (ele: IAdvancedSearchQuery) => ele?.filterType !== AdvancedSearchFilterType.Wrapper
                ) || [];
            newAdvancedQueryArr.push({
                filterType: AdvancedSearchFilterType.Wrapper,
                wrappers: selectedWrappers,
                investedInMandates: null,
                mandateNames: [],
                hasRegularPayments: null,
                hasRegularWithdrawals: null,
            });
            updateQuery(newAdvancedQueryArr);
        } else if (filterType == AdvancedSearchFilterType.Mandate) {
            const newAdvancedQueryArr =
                advancedQueryArr.filter(
                    (ele: IAdvancedSearchQuery) => ele?.filterType !== AdvancedSearchFilterType.Mandate
                ) || [];
            newAdvancedQueryArr.push({
                filterType: AdvancedSearchFilterType.Mandate,
                wrappers: [],
                investedInMandates: investedInMandate,
                mandateNames: mandateNames,
                hasRegularPayments: null,
                hasRegularWithdrawals: null,
            });
            updateQuery(newAdvancedQueryArr);
        } else if (filterType == AdvancedSearchFilterType.RegularPayment) {
            const newAdvancedQueryArr =
                advancedQueryArr.filter(
                    (ele: IAdvancedSearchQuery) => ele?.filterType !== AdvancedSearchFilterType.RegularPayment
                ) || [];
            newAdvancedQueryArr.push({
                filterType: AdvancedSearchFilterType.RegularPayment,
                wrappers: null,
                investedInMandates: null,
                mandateNames: [],
                hasRegularPayments: hasRegularPayments,
                hasRegularWithdrawals: null,
            });
            updateQuery(newAdvancedQueryArr);
        } else {
            const newAdvancedQueryArr =
                advancedQueryArr.filter(
                    (ele: IAdvancedSearchQuery) => ele?.filterType !== AdvancedSearchFilterType.RegularWithdrawal
                ) || [];
            newAdvancedQueryArr.push({
                filterType: AdvancedSearchFilterType.RegularWithdrawal,
                wrappers: null,
                investedInMandates: null,
                mandateNames: [],
                hasRegularPayments: null,
                hasRegularWithdrawals: hasRegularWithdrawals,
            });
            updateQuery(newAdvancedQueryArr);
        }
    };

    const updateQuery = (query: IAdvancedSearchQuery[]) => {
        setAdvancedQueryArr(query);
        props.applyAdancedFilter(query);
    };

    const renderAlerts = () => {
        const alerts = [];
        if (advancedQueryArr.length == 0) {
            return;
        }
        advancedQueryArr.forEach((advancedQuery) => {
            if (advancedQuery?.wrappers != null && advancedQuery?.wrappers?.length > 0) {
                if (advancedQuery?.wrappers?.length == productWrapperOptions.length) {
                    alerts.push({
                        message: "Wrapper: Select all",
                        type: AdvancedSearchFilterType.Wrapper,
                    });
                } else {
                    const labelArray = advancedQuery?.wrappers.map((ele) => {
                        return productWrapperOptions.filter((a) => a.type == ele)[0].label;
                    });
                    alerts.push({
                        message: "Wrapper: " + labelArray.join(),
                        type: AdvancedSearchFilterType.Wrapper,
                    });
                }
            }
            if (advancedQuery?.investedInMandates != null) {
                alerts.push({
                    message: advancedQuery?.investedInMandates ? "Mandate: Yes" : "Mandate: No",
                    type: AdvancedSearchFilterType.Mandate,
                });
                // if (advancedQuery?.mandateNames && advancedQuery?.mandateNames.length > 0) {
                //     advancedQuery?.mandateNames.map((ele) => {
                //         alerts.push({
                //             message: "Mandate: " + ele,
                //             type: AdvancedSearchFilterType.Mandate,
                //             detail: ele,
                //         });
                //     });
                // }
            }
            if (advancedQuery?.hasRegularPayments != null) {
                alerts.push({
                    message: advancedQuery?.hasRegularPayments ? "Regular payments: Yes" : "Regular payments: No",
                    type: AdvancedSearchFilterType.RegularPayment,
                });
            }
            if (advancedQuery?.hasRegularWithdrawals != null) {
                alerts.push({
                    message: advancedQuery?.hasRegularWithdrawals
                        ? "Regular withdrawals: Yes"
                        : "Regular withdrawals: No",
                    type: AdvancedSearchFilterType.RegularWithdrawal,
                });
            }
        });

        const alertsUI: JSX.Element[] = [];

        alerts.forEach((alert) => {
            alertsUI.push(
                <Alert
                    css="mr-10"
                    alertType={AlertType.Notification}
                    message={alert.message}
                    onAlertClose={() => cancelFilter(alert)}
                ></Alert>
            );
        });
        return alertsUI;
    };

    const cancelFilter = (alert) => {
        if (alert.type == AdvancedSearchFilterType.Wrapper) {
            setSelectedWrappers([]);
            const newAdvancedQueryArr =
                advancedQueryArr.filter(
                    (ele: IAdvancedSearchQuery) => ele?.filterType !== AdvancedSearchFilterType.Wrapper
                ) || [];
            updateQuery(newAdvancedQueryArr);
        } else if (alert.type == AdvancedSearchFilterType.Mandate) {
            // if (alert.detail != null) {
            //     let _data = mandateNames.filter((item) => item !== alert.detail);
            //     setMandateNames(_data);
            //     updateQuery({
            //         filterType: AdvancedSearchFilterType.Mandate,
            //         mandateNames: _data,
            //         investedInMandates: investedInMandate,
            //     });
            // } else {
            //     setInvestedInMandate(null);
            //     setMandateNames([]);
            //     updateQuery(null);
            // }
            setInvestedInMandate(null);
            setMandateNames([]);
            const newAdvancedQueryArr =
                advancedQueryArr.filter(
                    (ele: IAdvancedSearchQuery) => ele?.filterType !== AdvancedSearchFilterType.Mandate
                ) || [];
            updateQuery(newAdvancedQueryArr);
        } else if (alert.type == AdvancedSearchFilterType.RegularPayment) {
            setHasRegularPayments(null);
            const newAdvancedQueryArr =
                advancedQueryArr.filter(
                    (ele: IAdvancedSearchQuery) => ele?.filterType !== AdvancedSearchFilterType.RegularPayment
                ) || [];
            updateQuery(newAdvancedQueryArr);
        } else {
            setHasRegularWithdrawals(null);
            const newAdvancedQueryArr =
                advancedQueryArr.filter(
                    (ele: IAdvancedSearchQuery) => ele?.filterType !== AdvancedSearchFilterType.RegularWithdrawal
                ) || [];
            updateQuery(newAdvancedQueryArr);
        }
    };

    const renderDropdown = () => {
        const dropdownOptions: JSX.Element[] = [];
        advancedSearchFilterTypeArray.forEach((option) => {
            dropdownOptions.push(
                <div
                    className="item"
                    onClick={() => {
                        setContent(advancedSearchFilterTypeMap[option]);
                    }}
                >
                    {advancedSearchFilterTypeMap[option]}
                </div>
            );
        });
        return dropdownOptions;
    };

    const wrapperOptions = () => {
        const elements: JSX.Element[] = [];
        productWrapperOptions.forEach((element) => {
            elements.push(
                <CheckboxEditor
                    label={element.label}
                    value={selectedWrappers.some((item) => item == element.type)}
                    cssWrapper="checkbox-panel"
                    onChange={(v) => {
                        if (v) {
                            const _data = selectedWrappers.concat([element.type]);
                            setSelectedWrappers(_data);
                        } else {
                            const _data = selectedWrappers.filter((item) => item !== element.type);
                            setSelectedWrappers(_data);
                        }
                    }}
                />
            );
        });
        return elements;
    };

    const advancedDropDownCss = () => {
        return showContent == "dropdown" ? "mb-20" : "item-is-clicked mb-20";
    };

    const closeFilter = () => {
        setIsOpen(false);
        setContent("dropdown");
    };

    const renderFilterContent = (option: AdvancedSearchFilterType) => {
        if (option == AdvancedSearchFilterType.Wrapper) {
            return (
                <>
                    <CheckboxEditor
                        label="Select all"
                        value={selectedWrappers.length == 5}
                        cssWrapper="checkbox-panel"
                        onChange={(v) => {
                            if (v) {
                                setSelectedWrappers([
                                    SubAccountType.StocksIsa,
                                    SubAccountType.InvestmentAccount,
                                    SubAccountType.Sipp,
                                    SubAccountType.OnshoreBond,
                                    SubAccountType.OffshoreBond,
                                    SubAccountType.Npr,
                                    SubAccountType.JuniorSipp,
                                ]);
                            } else {
                                setSelectedWrappers([]);
                            }
                        }}
                    />
                    {wrapperOptions()}
                </>
            );
        } else if (option == AdvancedSearchFilterType.Mandate) {
            return (
                <div>
                    <RadiosEditor
                        cssWrapper="rg-inline mb-16"
                        label={"Search for accounts that are invested in a mandate?"}
                        selected={investedInMandate}
                        options={[true, false]}
                        getText={(v) => (v ? "Yes" : "No")}
                        onChange={(v) => {
                            setErrorMsg([]);
                            setInvestedInMandate(v);
                            if (!v) {setMandateNames([]);}
                        }}
                    />
                    {investedInMandate && (
                        <SearchInput
                            apiClient={(v) => {
                                return new MandateNameFuzzySearchClient().get(v);
                            }}
                            onClick={(v: Mandate) => {
                                if (!v) {return;}
                                setErrorMsg([]);
                                setMandateNames(mandateNames.concat([v?.MandateName]));
                            }}
                            getText={(v: Mandate) => v?.MandateName}
                            cssWrapper="control-size-430"
                            label={"Mandate name"}
                        />
                    )}
                    {mandateNames.length > 0 &&
                        mandateNames.map((mandateName) => (
                            <>
                                <Alert
                                    alertType={AlertType.Notification}
                                    message={mandateName}
                                    onAlertClose={() => {
                                        const _data = mandateNames.filter((item) => item !== mandateName);
                                        setMandateNames(_data);
                                    }}
                                ></Alert>
                            </>
                        ))}
                </div>
            );
        } else if (option == AdvancedSearchFilterType.RegularPayment) {
            return (
                <RadiosEditor
                    cssWrapper="rg-inline mb-16"
                    label={"Regular payments set up"}
                    selected={hasRegularPayments}
                    options={[true, false]}
                    getText={(v) => (v ? "Yes" : "No")}
                    onChange={(v) => setHasRegularPayments(v)}
                />
            );
        } else if (option == AdvancedSearchFilterType.RegularWithdrawal) {
            return (
                <RadiosEditor
                    cssWrapper="rg-inline mb-16"
                    label={"Regular withdrawals set up"}
                    selected={hasRegularWithdrawals}
                    options={[true, false]}
                    getText={(v) => (v ? "Yes" : "No")}
                    onChange={(v) => setHasRegularWithdrawals(v)}
                />
            );
        }
    };

    const checkValid = (element: AdvancedSearchFilterType) => {
        if (element == AdvancedSearchFilterType.Mandate) {
            if (investedInMandate == true && mandateNames.length == 0) {
                setErrorMsg(["Please at least select one mandate"]);
                return false;
            }
        }
        return true;
    };

    const renderFilterBody = () => {
        const elements: JSX.Element[] = [];
        advancedSearchFilterTypeArray.forEach((element) => {
            elements.push(
                <>
                    {showContent == advancedSearchFilterTypeMap[element] && (
                        <>
                            <div className="header">
                                <div className="row">
                                    <div className="col-6">
                                        <h2>{advancedSearchFilterTypeMap[element]}</h2>
                                    </div>
                                    <div className="col-6 col-right col-middle">
                                        <button type="button" className="close-btn" onClick={() => closeFilter()}>
                                            <span className="sr-only">Close</span>
                                            <span className="icon" aria-hidden="true"></span>
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div className="content">
                                <fieldset className="form-group control-size-430 mb-18">
                                    <div className="controls-wrap">{renderFilterContent(element)}</div>
                                </fieldset>
                                {errorMsg.length > 0 &&
                                    errorMsg.map((msg) => {
                                        return <Alert alertType={AlertType.Error} message={msg}></Alert>;
                                    })}
                                <div className="row row-right">
                                    <div className="col-auto">
                                        <button
                                            className="btn primary"
                                            onClick={() => {
                                                if (checkValid(element)) {
                                                    setErrorMsg([]);
                                                    applyFilter(element);
                                                    closeFilter();
                                                }
                                            }}
                                        >
                                            Apply
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </>
            );
        });
        return elements;
    };

    return (
        <>
            <AdvancedQueryDropdown
                isOpen={isOpen}
                togglePopUp={(v) => setIsOpen(v)}
                label="Advanced query"
                cssWrapper={advancedDropDownCss()}
            >
                {showContent == "dropdown" && renderDropdown()}
                {renderFilterBody()}
            </AdvancedQueryDropdown>

            <div>{renderAlerts()}</div>
        </>
    );
};
